import { logger, useAnalytics, useAuthentication } from '@ecosystem/newron-client-lib';
import { useCallback, useMemo } from 'react';

import { postUpsellAnalytics } from '../services/Market.service';
import { QualtricsRequestBody, postQualtricsData } from '../services/Qualtrics.service';

export type UpsellAnalytics = {
  cell?: string;
  dataPeriod?: string;
  upsellName: string;
  upsellStatus: string;
};

export type AmplitudeTracking = {
  pageName: string;
  cell: string;
};

export interface EmbeddedQualtricsData {
  market: string;
  country: string;
  userGroupIds?: string;
  userGroupNames?: string;
  clientName: string;
  clientId: number;
  urlTokenId?: string;
  key?: string;
}

/**
 * Hook for interacting with Qualtrics API, handling analytics tracking and Qualtrics workflow triggers.
 * @returns An object containing the triggerQualtricsWorkflow function
 * @returns {Object} hook methods
 * @returns {Function} hook methods.triggerQualtricsWorkflow - Triggers Qualtrics workflow with analytics tracking
 * @example
 * const { triggerQualtricsWorkflow } = useQualtricsAPI();
 * await triggerQualtricsWorkflow({
 *   trackingText: "event_name",
 *   triggerQualtrics: true,
 *   embeddedData: data,
 *   triggerAnalytics: true,
 *   trackingBody: amplitudeData,
 *   analyticsData: upsellData
 * });
 */
export const useQualtricsAPI = (): {
  triggerQualtricsWorkflow: (params: {
    trackingText: string;
    triggerQualtrics: boolean;
    embeddedData: EmbeddedQualtricsData;
    triggerAnalytics?: boolean;
    trackingBody: AmplitudeTracking;
    analyticsData?: UpsellAnalytics;
  }) => Promise<void>;
} => {
  const { track } = useAnalytics();
  const { getAccessToken } = useAuthentication();

  const triggerQualtricsWorkflow = useCallback(
    async ({
      trackingText,
      triggerQualtrics,
      embeddedData,
      trackingBody,
      analyticsData,
    }: {
      trackingText: string;
      triggerQualtrics: boolean;
      embeddedData: EmbeddedQualtricsData;
      trackingBody: AmplitudeTracking;
      analyticsData?: UpsellAnalytics;
    }) => {
      const token = await getAccessToken();

      const body: QualtricsRequestBody = {
        advance: true,
        embeddedData,
        response: triggerQualtrics,
      };
      if (triggerQualtrics) {
        logger.info('upsell-activated', body.embeddedData.userid);
        await postQualtricsData(body, token);
      }

      if (analyticsData) {
        await postUpsellAnalytics(token, analyticsData);
      } else {
        track(trackingText, trackingBody);
      }
    },
    [getAccessToken, track]
  );

  return useMemo(
    () => ({
      triggerQualtricsWorkflow,
    }),
    [triggerQualtricsWorkflow]
  );
};
